import React from 'react'
import PropTypes from 'prop-types'
import { ArrowLeft, Trash2, RefreshCw, FolderOpen, Clock, CheckCircle, AlertCircle, Eye } from 'lucide-react'
import { ApprovalStatusBadge } from '@components/features/projects'

/**
 * Project Header Component
 *
 * Displays project title, status, priority badges, and action buttons
 * Note: Edit button has been moved to individual tab sections for better UX
 * Note: Progress bar has been moved to the Overview tab
 */
const ProjectHeader = ({ project, onBack, onDelete, onRefresh }) => {
	// Get status display info
	const getStatusInfo = (status) => {
		const statusMap = {
			planning: { label: 'Planning', className: 'status--info', icon: Clock },
			in_progress: { label: 'In Progress', className: 'status--primary', icon: RefreshCw },
			review: { label: 'Under Review', className: 'status--warning', icon: Eye },
			completed: { label: 'Completed', className: 'status--success', icon: CheckCircle },
			on_hold: { label: 'On Hold', className: 'status--secondary', icon: AlertCircle },
			cancelled: { label: 'Cancelled', className: 'status--danger', icon: AlertCircle },
		}
		return statusMap[status] || { label: status, className: 'status--secondary', icon: Clock }
	}

	// Get priority display info
	const getPriorityInfo = (priority) => {
		const priorityMap = {
			critical: { label: 'Critical', className: 'priority--critical' },
			high: { label: 'High', className: 'priority--high' },
			medium: { label: 'Medium', className: 'priority--medium' },
			low: { label: 'Low', className: 'priority--low' },
		}
		return priorityMap[priority] || { label: priority, className: 'priority--medium' }
	}

	const statusInfo = getStatusInfo(project.status)
	const priorityInfo = getPriorityInfo(project.priority)
	const StatusIcon = statusInfo.icon

	return (
		<div className="project-header">
			<div className="project-header__top">
				<div>
					<button className="project-header__back" onClick={onBack}>
						<ArrowLeft size={16} />
						Back to Projects
					</button>

					<div className="project-header__title-row">
						<div className="project-header__icon">
							<FolderOpen size={28} />
						</div>
						<div className="project-header__info">
							<h1 className="project-header__title">{project.name}</h1>
							<div className="project-header__badges">
								<span className={`project-status ${statusInfo.className}`}>
									<StatusIcon size={12} />
									{statusInfo.label}
								</span>
								<span className={`project-priority ${priorityInfo.className}`}>{priorityInfo.label}</span>
								{project.type && (
									<span className="project-status status--secondary">{project.type.charAt(0).toUpperCase() + project.type.slice(1)}</span>
								)}
								{project.approval_status && (
									<ApprovalStatusBadge status={project.approval_status} />
								)}
							</div>
						</div>
					</div>
				</div>

				<div className="project-header__actions">
					<button className="project-header__action-btn" onClick={onRefresh} title="Refresh">
						<RefreshCw size={16} />
					</button>
					{onDelete && (
						<button className="project-header__action-btn project-header__action-btn--danger" onClick={onDelete}>
							<Trash2 size={16} />
							Delete
						</button>
					)}
				</div>
			</div>
		</div>
	)
}

ProjectHeader.propTypes = {
	project: PropTypes.shape({
		name: PropTypes.string.isRequired,
		status: PropTypes.string,
		priority: PropTypes.string,
		type: PropTypes.string,
		approval_status: PropTypes.string,
	}).isRequired,
	onBack: PropTypes.func.isRequired,
	onDelete: PropTypes.func,
	onRefresh: PropTypes.func.isRequired,
}

export default ProjectHeader
